import React from 'react'
import { useTheme } from '@/hooks/useTheme'

interface ReportCardProps {
  title: string
  subtitle?: string
  chartComponent: React.ReactNode
  tableComponent: React.ReactNode
  loading?: boolean
  error?: string
  onRefresh?: () => void
  onExport?: () => void
  children?: React.ReactNode
}

const ReportCard: React.FC<ReportCardProps> = ({
  title,
  subtitle,
  chartComponent,
  tableComponent,
  loading = false,
  error,
  onRefresh,
  onExport,
  children
}) => {
  const { colors } = useTheme()

  if (error) {
    let errorMessage = error
    const lowerError = error.toLowerCase()

    // Detectar tipos de error comunes
    if (lowerError.includes('permiso') || lowerError.includes('forbidden') || lowerError.includes('no tiene permiso')) {
      errorMessage = 'No tienes permisos para ver este reporte'
    } else if (lowerError.includes('sesión') || lowerError.includes('unauthorized') || lowerError.includes('token')) {
      errorMessage = 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente'
    }

    return (
      <div
        className="rounded-lg p-6 border-l-4"
        style={{ borderColor: colors.primaryDark, backgroundColor: `${colors.primaryDark}15` }}
      >
        <h3 className="font-semibold mb-2" style={{ color: colors.primaryDark }}>
          {title}
        </h3>
        <p className="text-sm" style={{ color: colors.primaryDark }}>
          {errorMessage}
        </p>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
      {/* Header */}
      <div className="px-4 py-2 border-b border-gray-100" style={{ backgroundColor: `${colors.primary}15` }}>
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1">
            <h3 className="text-base font-semibold" style={{ color: colors.primary }}>
              {title}
            </h3>
            {subtitle && <p className="text-xs text-gray-600 mt-0.5">{subtitle}</p>}
          </div>

          {/* Acciones */}
          <div className="flex gap-2">
            {onRefresh && (
              <button
                onClick={onRefresh}
                disabled={loading}
                className="p-1.5 rounded-lg hover:bg-gray-100 transition-all disabled:opacity-50"
                title="Actualizar"
              >
                🔄
              </button>
            )}
            {onExport && (
              <button
                onClick={onExport}
                className="p-1.5 rounded-lg hover:bg-gray-100 transition-all"
                title="Descargar CSV"
              >
                📥
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Contenido */}
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div
                className="w-8 h-8 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-2"
              ></div>
              <p className="text-gray-600 text-sm">Cargando datos...</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Gráfico */}
            <div className="w-full overflow-hidden">
              {chartComponent}
            </div>

            {/* Tabla */}
            <div className="w-full">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Datos detallados</h4>
              {tableComponent}
            </div>

            {children && <div className="mt-4">{children}</div>}
          </div>
        )}
      </div>
    </div>
  )
}

export default ReportCard
